import Sb from "../abstract/StatefulBehavior";
import classNames from "classnames";
import { ResizeObserver as RoPolyfill } from "@juggle/resize-observer";
import { focusable as domFocusable } from "../../helpers/utilities";
import tokens from "tokens";
import { showSecondaryNav, hideSecondaryNav } from "./overlayHelpers";

// Polyfill ResizeObserve only if unsupported (Safari, IE11)
const ResizeObserver = (window && window.ResizeObserver) || RoPolyfill;

export default class ToggleOverlay extends Sb {
  constructor(el, props, refs) {
    super();

    this.state = {
      open: false,
    };

    this.el = el;
    this.props = props;
    this.refs = refs;

    if (!refs.overlayToggle || !refs.overlay) return false;

    this.initToggleClasses = this.overlayToggles.map(
      (toggle) => toggle.className
    );
    this.initOverlayClass = refs.overlay.className;
    this.initBodyClass = document.body.className;

    this.resizeObserver = new ResizeObserver(this.handleResize);

    this.update();
    this.bindEvents();
  }

  get overlayToggles() {
    const { overlayToggle } = this.refs;
    return Array.isArray(overlayToggle) ? overlayToggle : Array(overlayToggle);
  }

  get overlayFocusable() {
    return domFocusable(this.refs.overlay);
  }

  get allFocusable() {
    return this.overlayToggles.concat(this.overlayFocusable);
  }

  get hideOnDesktop() {
    return this.props.hideOnDesktop;
  }

  get artistSearchInput() {
    return this.el.querySelector("input.m-artist-search__input");
  }

  get siblingNav() {
    return document.querySelector(".m-sibling-nav--mobile-visible");
  }

  update = () => {
    const { open } = this.state;
    const { overlay } = this.refs;

    this.overlayToggles.forEach((toggle, index) => {
      toggle.setAttribute("aria-expanded", open);
      toggle.className = classNames(this.initToggleClasses[index], {
        "is-toggled": open,
        "is-untoggled": !open,
      });
    });

    this.overlayFocusable.forEach((el) =>
      el.setAttribute("tabindex", open ? 0 : -1)
    );

    if (!this.props.onClose) {
      document.body.className = classNames(this.initBodyClass, {
        "has-scroll-lock": open,
      });
    }
  };

  bindEvents() {
    this.overlayToggles.forEach((toggle) =>
      toggle.addEventListener("click", this.handleClick)
    );
  }

  handleClick = (event) => {
    this.state.open ? this.closeOverlay() : this.openOverlay();
  };

  openOverlay = () => {
    window.addEventListener("keydown", this.handleKeyDown);
    this.refs.overlay.className = classNames(this.initOverlayClass, "is-open");
    this.setState({ open: true });
    if (this.hideOnDesktop) this.resizeObserver.observe(this.el);
    if (this.props.onOpen) this.props.onOpen();
    if (this.artistSearchInput) {
      setTimeout(() => {
        this.artistSearchInput.focus();
      }, 500);
    }
    hideSecondaryNav();
  };

  closeOverlay = () => {
    if (!this.state.open) return null;

    window.removeEventListener("keydown", this.handleKeyDown);
    this.refs.overlay.className = classNames(
      this.initOverlayClass,
      "is-closed"
    );
    this.setState({ open: false });
    if (this.hideOnDesktop) this.resizeObserver.unobserve(this.el);
    if (this.props.onClose) this.props.onClose();

    showSecondaryNav(this.refs.overlay);
  };

  handleResize = (entries) => {
    const getWidth = (entry) => {
      if (entry.contentBoxSize.length) {
        return entry.contentBoxSize[0].inlineSize;
      }
      // needed for FF <92 which implements contentBoxSize as a single object
      if (entry.contentBoxSize) {
        return entry.contentBoxSize.inlineSize;
      }
      return entry.contentRect.width;
    };

    entries.forEach((entry) => {
      const width = getWidth(entry);
      if (!this.state.open || width < tokens.breakpoints.mobileNav) return;
      // close overlay if it's open and becomes wider than the breakpoint
      this.closeOverlay();
    });
  };

  handleKeyDown = (event) => {
    const { key, shiftKey } = event;
    if (key !== "Escape" && key !== "Tab") return;
    key === "Escape" && this.closeOverlay();
    key === "Tab" && this.handleTab(event);
  };

  handleTab(event) {
    if (!this.overlayFocusable || !this.overlayFocusable.length) return false;

    const focusedItem = document.activeElement;
    const focusableIndex = this.allFocusable.indexOf(focusedItem);

    const lastIndex = event.shiftKey
      ? focusableIndex === 0
      : focusableIndex === this.allFocusable.length - 1;

    // if tab or shift + tab will remain within this.allFocusable,
    // proceed with default key behavior
    if (!lastIndex) return;

    // if not, prevent default and jump to other end of this.allFocusable
    event.preventDefault();
    const nextFocus = event.shiftKey
      ? this.allFocusable[this.allFocusable.length - 1]
      : this.allFocusable[0];
    nextFocus.focus();
  }
}
